/**
 * Geohash — the one implementation both the browser and the Worker use.
 *
 * The client truncates a position with it, the server validates and
 * aggregates with it. Two implementations that disagreed at a cell boundary
 * would be a bug nobody would find (SPEC §10.1), so there is exactly one.
 */

export const ALPHABET = '0123456789bcdefghjkmnpqrstuvwxyz';
/** The character after `z` in byte order — makes `cell < prefix + '{'` a prefix range scan. */
export const AFTER_Z = '{';

const DECODE: Record<string, number> = {};
for (let i = 0; i < ALPHABET.length; i++) DECODE[ALPHABET[i]!] = i;

export const CELL_RE = /^[0-9bcdefghjkmnpqrstuvwxyz]+$/;

export interface Bounds {
  minLat: number;
  minLon: number;
  maxLat: number;
  maxLon: number;
}

export function isValidCell(s: unknown, precision?: number): s is string {
  if (typeof s !== 'string' || s.length === 0 || !CELL_RE.test(s)) return false;
  return precision === undefined || s.length === precision;
}

export function encode(lat: number, lon: number, precision: number): string {
  if (!(precision >= 1 && precision <= 12)) throw new RangeError(`precision ${precision}`);
  if (!(lat >= -90 && lat <= 90)) throw new RangeError(`lat ${lat}`);
  // Normalise longitude into [-180, 180).
  lon = ((((lon + 180) % 360) + 360) % 360) - 180;
  let minLat = -90, maxLat = 90, minLon = -180, maxLon = 180;
  let out = '';
  let bit = 0, ch = 0, even = true;
  while (out.length < precision) {
    if (even) {
      const mid = (minLon + maxLon) / 2;
      if (lon >= mid) { ch = (ch << 1) | 1; minLon = mid; } else { ch <<= 1; maxLon = mid; }
    } else {
      const mid = (minLat + maxLat) / 2;
      if (lat >= mid) { ch = (ch << 1) | 1; minLat = mid; } else { ch <<= 1; maxLat = mid; }
    }
    even = !even;
    if (++bit === 5) { out += ALPHABET[ch]; bit = 0; ch = 0; }
  }
  return out;
}

export function bounds(hash: string): Bounds {
  if (!isValidCell(hash)) throw new RangeError(`bad geohash ${JSON.stringify(hash)}`);
  let minLat = -90, maxLat = 90, minLon = -180, maxLon = 180;
  let even = true;
  for (const c of hash) {
    const v = DECODE[c]!;
    for (let n = 4; n >= 0; n--) {
      const bit = (v >> n) & 1;
      if (even) {
        const mid = (minLon + maxLon) / 2;
        if (bit) minLon = mid; else maxLon = mid;
      } else {
        const mid = (minLat + maxLat) / 2;
        if (bit) minLat = mid; else maxLat = mid;
      }
      even = !even;
    }
  }
  return { minLat, minLon, maxLat, maxLon };
}

/** Centre of the cell. */
export function decode(hash: string): { lat: number; lon: number } {
  const b = bounds(hash);
  return { lat: (b.minLat + b.maxLat) / 2, lon: (b.minLon + b.maxLon) / 2 };
}

export type Direction = 'n' | 'e' | 's' | 'w';

/**
 * The adjacent cell in a direction, or null past a pole. Longitude wraps at
 * the antimeridian.
 */
export function neighbour(hash: string, dir: Direction): string | null {
  const b = bounds(hash);
  const h = b.maxLat - b.minLat, w = b.maxLon - b.minLon;
  let lat = (b.minLat + b.maxLat) / 2, lon = (b.minLon + b.maxLon) / 2;
  switch (dir) {
    case 'n': lat += h; break;
    case 's': lat -= h; break;
    case 'e': lon += w; break;
    case 'w': lon -= w; break;
  }
  if (lat > 90 || lat < -90) return null;
  return encode(lat, lon, hash.length);
}

export function neighbours(hash: string): Record<Direction, string | null> {
  return { n: neighbour(hash, 'n'), e: neighbour(hash, 'e'), s: neighbour(hash, 's'), w: neighbour(hash, 'w') };
}

/** Cell size in degrees for a precision. */
export function cellSize(precision: number): { latDeg: number; lonDeg: number } {
  const bits = precision * 5;
  const lonBits = Math.ceil(bits / 2), latBits = Math.floor(bits / 2);
  return { latDeg: 180 / 2 ** latBits, lonDeg: 360 / 2 ** lonBits };
}

/**
 * Every cell of `precision` intersecting a bbox, or null when there would be
 * more than `limit`. A bbox with minLon > maxLon crosses the antimeridian.
 */
export function cellsInBBox(bbox: Bounds, precision: number, limit = 1024): string[] | null {
  const { latDeg, lonDeg } = cellSize(precision);
  const minLat = Math.max(-90, bbox.minLat), maxLat = Math.min(90, bbox.maxLat);
  if (minLat > maxLat) return [];
  const spans: Array<[number, number]> =
    bbox.minLon <= bbox.maxLon ? [[bbox.minLon, bbox.maxLon]] : [[bbox.minLon, 180], [-180, bbox.maxLon]];
  const nRows = Math.round(180 / latDeg), nCols = Math.round(360 / lonDeg);
  const out = new Set<string>();
  for (const [lo, hi] of spans) {
    // Row/column indices, clamped so lat 90 / lon 180 land in the last cell.
    const r0 = Math.floor((minLat + 90) / latDeg), r1 = Math.min(nRows - 1, Math.floor((maxLat + 90) / latDeg));
    const c0 = Math.floor((lo + 180) / lonDeg), c1 = Math.min(nCols - 1, Math.floor((hi + 180) / lonDeg));
    const rows = r1 - r0 + 1, cols = c1 - c0 + 1;
    if (rows * cols + out.size > limit) return null;
    // Step through cell centres so floating error at the seam cannot skip a cell.
    const lat0 = (r0 + 0.5) * latDeg - 90;
    const lon0 = (c0 + 0.5) * lonDeg - 180;
    for (let r = 0; r < rows; r++) {
      const lat = lat0 + r * latDeg;
      for (let c = 0; c < cols; c++) {
        const lon = lon0 + c * lonDeg;
        out.add(encode(lat, lon, precision));
        if (out.size > limit) return null;
      }
    }
  }
  return [...out];
}
